import { useContext, useEffect } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import {
    BannerType,
    BannerView,
    colors
} from "@dotnet/telematics-design-system";

import Tag, { TAG_COLORS, TAG_SIZES } from "components/DesignComponents/Tag";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import TriSourceContext from "context/triSource/triSourceContext";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import {
    getFilterParamsWithoutRoot,
    getStringifiedParams
} from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { useDecoder } from "shared/hooks/useDecoder";
import { useFormattedNumber } from "shared/hooks/useFormattedNumber";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { body2SemiBoldStyles } from "shared/styles/typography";
import { TRI_SOURCES } from "shared/types/enums";

import FilteredTagLoader from "../loaders/FilteredTagLoader";
import { TriSourceTabProps } from "../types";

const Filtered = ({ description, setCount }: TriSourceTabProps) => {
    const { gray700 } = colors;

    const { filterQueryParams } = useContext(ParamsContext);
    const { tableName, rowsPerPage } = useContext(TableContext);

    const { filteredOnlyCount, handleNeedConfirmation, setFilteredOnlyCount } =
        useContext(TriSourceContext);

    const { t } = useTranslation();
    const { cancelSource, isCanceled, getData } = useApi();
    const { renderFormattedNumber } = useFormattedNumber();
    const { decodeStrings } = useDecoder();
    const isMounted = useIsMounted();

    const activeFiltersExist = Object.keys(filterQueryParams).length > 0;

    // Check if confirmation is required
    useEffect(() => {
        handleNeedConfirmation(Number(filteredOnlyCount) > 20);
        setCount?.(Number(filteredOnlyCount));
    }, [filteredOnlyCount, handleNeedConfirmation, setCount]);

    // Get the number of filtered items, excluding the search query
    useEffect(() => {
        const getCount = async () => {
            const stringifiedFilterParams =
                getStringifiedParams(filterQueryParams);

            try {
                const {
                    data: { total }
                } = await getData(
                    `${tableName}?${decodeStrings(
                        stringifiedFilterParams
                    )}&per_page=${rowsPerPage}`
                );

                if (isMounted) {
                    setFilteredOnlyCount(total);
                }
            } catch (error) {
                if (isMounted && !isCanceled(error)) {
                    setFilteredOnlyCount(0);
                }
            }
        };

        activeFiltersExist && filteredOnlyCount === null && getCount();

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };

        // eslint-disable-next-line
    }, []);

    const getFilterNames = () => {
        const filterParamsWithoutRoot =
            getFilterParamsWithoutRoot(filterQueryParams);

        return Object.keys(filterParamsWithoutRoot);
    };

    const title = `${t(
        `Dialog##filtered ${tableName}`
    )}: ${renderFormattedNumber(Number(filteredOnlyCount))}`;

    const filterNames = getFilterNames();

    return (
        <div data-testid={`tri-source-${TRI_SOURCES.Filtered}`}>
            <BannerView
                bannerType={BannerType.Info}
                title={t(
                    `Dialog##tri source##filtered tab description##${description}`
                )}
                css={css({
                    marginBottom: "16px"
                })}
            />

            {filteredOnlyCount === null ? (
                <FilteredTagLoader />
            ) : (
                <Tag
                    size={TAG_SIZES.Medium}
                    color={TAG_COLORS.Primary}
                    title={title}
                />
            )}

            <div
                css={css({
                    marginTop: "16px",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "4px"
                })}
            >
                <span css={[body2SemiBoldStyles, { color: gray700 }]}>
                    {t("Filter##filters used")}:
                </span>

                {filterNames.map(filterName => (
                    <Tag
                        key={filterName}
                        size={TAG_SIZES.Medium}
                        color={TAG_COLORS.Primary}
                        title={t(`Filter##${filterName}`)}
                    />
                ))}
            </div>
        </div>
    );
};

export default Filtered;
